import { compareOrderIndexes } from '@gitbook/util-orderindex';

import type { RevisionTagGroup, RevisionTagGroups } from '../types';

/**
 * Sort comparator for sibling groups: by stored position, then by slug when two groups share a
 * position (e.g. both inserted at the same spot on two branches), so every client shows the same
 * order.
 */
export function compareTagGroupOrder(a: RevisionTagGroup, b: RevisionTagGroup): number {
    return compareOrderIndexes(a, b) || (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0);
}

/** The direct subcategories of `parent` (top-level groups when omitted), in display order. */
export function getTagGroupChildren(groups: RevisionTagGroups | undefined, parent?: string) {
    return Object.values(groups ?? {})
        .filter((group) => group.parent === parent)
        .sort(compareTagGroupOrder);
}

/**
 * Every tag group as one flat list in sidebar order: each category followed by its subcategories
 * (depth-first), siblings by position. E.g. `Product`, `Product > Features`, `Product > Pricing`,
 * `Guides`. Groups whose parent is missing or loops back are appended at the end, so none are
 * dropped.
 */
export function getOrderedTagGroups(groups: RevisionTagGroups | undefined): RevisionTagGroup[] {
    const all = Object.values(groups ?? {});
    const result: RevisionTagGroup[] = [];
    const visited = new Set<string>();
    const children = new Map<string | undefined, RevisionTagGroup[]>();
    for (const group of all) {
        const siblings = children.get(group.parent) ?? [];
        siblings.push(group);
        children.set(group.parent, siblings);
    }
    for (const siblings of children.values()) {
        siblings.sort(compareTagGroupOrder);
    }
    // A stack instead of recursion: nesting depth has no limit.
    const walk = (roots: RevisionTagGroup[]) => {
        const stack = [...roots].reverse();
        while (stack.length) {
            const group = stack.pop()!;
            if (visited.has(group.slug)) continue;
            visited.add(group.slug);
            result.push(group);
            stack.push(...[...(children.get(group.slug) ?? [])].reverse());
        }
    };
    walk(children.get(undefined) ?? []);
    if (result.length < all.length) {
        // Unreached groups have a missing parent or sit in a loop: start from the missing-parent
        // ones, then break each remaining loop at its first group.
        walk(
            all
                .filter(
                    (group) => group.parent !== undefined && !Object.hasOwn(groups!, group.parent)
                )
                .sort(compareTagGroupOrder)
        );
        walk([...all].sort(compareTagGroupOrder));
    }
    return result;
}

/**
 * Whether two versions of a group look the same to a user (name, place in the tree, position).
 * Who edited it is ignored.
 */
export function compareTagGroups(a: RevisionTagGroup, b: RevisionTagGroup): boolean {
    return (
        a.slug === b.slug &&
        a.label === b.label &&
        a.parent === b.parent &&
        a.orderIndex === b.orderIndex
    );
}

/**
 * Whether `parent` is `slug` itself or sits anywhere below it, i.e. whether moving `slug` into
 * `parent` would put a category inside itself.
 */
export function isTagGroupDescendant(
    groups: RevisionTagGroups | undefined,
    slug: string,
    parent: string
) {
    const visited = new Set<string>();
    let current: string | undefined = parent;
    while (current) {
        if (current === slug) return true;
        // The chain loops without reaching `slug`: that cycle doesn't involve it.
        if (visited.has(current)) return false;
        visited.add(current);
        current = groups?.[current]?.parent;
    }
    return false;
}
